// Pure mapping from a facade snapshot to the backend `BcmBookingDto` shape.
// Kept side-effect free (no facade/HTTP access) so it is trivially unit
// testable; the commit service builds the snapshot and POSTs the result.
//
// Scope: standard wizard (date / berth / person) plus the advanced services
// tab (one-off positions + product subscriptions), electric-meter assignments,
// key lendings and document attachments. Existing person/boat/key are
// referenced by id; new ones send their create fields (no id) for backend
// nested create. Documents are uploaded to bcm_files when picked and referenced
// here by id — see project_booking_v2_commit.

import { v4 as uuidv4 } from 'uuid';

import { getVestingPeriodUntil } from '@shared/functions/vesting-period-helper';
import { BcmDynamicPriceDto } from '@shared/models/bcm-dynamic-price';
import { DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';
import { shouldOmitZeroPriceBerthPosition } from '@shared/utils/berth-product-booking.util';

import { keyHasPersistedBorrower, mapKeyDepositPositions } from '../utils/booking-v2-deposit-billing';
import { mapTenantRelationPositions } from '../utils/booking-v2-relation-billing';
import { winterStorageAssignmentPatch } from '../utils/booking-v2-winter-storage';
import { serviceFollowsAutoName } from '../utils/booking-v2-service-naming';
import {
    BookingV2BerthOccupancy,
    BookingV2BoatOption,
    BookingV2BookingDocument,
    BookingV2ElectricMeterAssignment,
    BookingV2KeyAssignment,
    BookingV2PersonOption,
    BookingV2RelationLineOverrides,
    BookingV2RelationOption,
    BookingV2Service,
    BookingV2SubscriptionConfig,
    BookingV2Traveler,
} from '../dtos/booking-v2.dtos';
import type { BookingV2DateRange, BookingV2EditBaseline, BookingV2Type } from './booking-v2.facade';
import { resolveOccupancyCommitWindow } from '../utils/booking-v2-berth-occupancy-dates.util';

// Client-side mirror of the subset of the backend `BcmBookingDto` that the
// step-1 commit populates. Decoupled from the legacy @shared/models classes.
export interface BookingV2CommitDto {
    general: {
        bookingType: BookingV2Type;
        note?: string;
        status: { id: number };
        personOrCompany: 'person' | 'company';
        isBoatOwner: boolean;
        // Reservation only: the confirm-by deadline ("Reservierung gültig bis").
        reservedUntil?: Date | null;
        // Reservation only: free-text note shown on the reservation.
        reservationText?: string;
        bcm_orders_id?: number | null;
    };
    // Existing → `{ id }`. New (inline create) → fields without `id`; the
    // backend creates the entity in the booking transaction.
    person?: BookingV2CommitPersonDto;
    company?: BookingV2CommitCompanyDto;
    boat: BookingV2CommitBoatDto;
    berthAssignments: BookingV2BerthAssignmentDto[];
    travelers: { firstName: string; lastName: string }[];
    tenantRelationAssignment?: BookingV2TenantRelationDto;
    // One-off service lines (the services tab, non-subscription rows).
    positions: BookingV2PositionDto[];
    // Recurring service lines (the services tab, subscription rows).
    subscriptions: BookingV2SubscriptionDto[];
    // Electric-meter assignments (the power tab). The backend auto-creates the
    // billing invoice position from productType + product + quantity.
    electricMeterAssignments: BookingV2ElectricMeterAssignmentDto[];
    // Key lendings (the keys tab) → person/boat key assignments. New keys carry
    // create fields so the backend inserts the catalog key first.
    keyAssignments: BookingV2KeyAssignmentDto[];
    // Document attachments (the documents tab): new rows, kept rows as a reference
    // and explicit removals.
    documentAssignments: BookingV2DocumentAssignmentDto[];
}

export type BookingV2DocumentAssignmentDto =
    | {
        // id of the already-uploaded bcm_files row.
        bcmFilesId: number;
        // bcm_documents.title — the user-entered name (backend falls back to the file name).
        title: string;
        // null → the backend files it under "Sonstiges".
        documentCategoryId: number | null;
    }
    // Edit mode: an existing row the user kept. The backend skips it; a backend that
    // still deletes by absence (deployed before 7176) keeps it because its file is listed.
    | { id: number; bcmFilesId: number; title: string }
    // Edit mode: an existing bcm_documents row the user removed.
    | { id: number; deleted: true };

export interface BookingV2KeyAssignmentDto {
    uuid: string;
    // Edit mode: the existing lending row id → patch it; with `deleted` → remove it.
    id?: number;
    deleted?: boolean;
    // Existing catalog key → `{ id }`. New (inline) → `{ id: null }` plus the
    // create fields below; the backend creates the catalog key in the booking
    // transaction.
    key: { id: number | null };
    keyName?: string;
    // Free text server-side. New keys send the wizard's fixed type token.
    keyType?: string | null;
    keyIdentifier?: string | null;
    isUnique?: boolean;
    // Links the new key to its deposit (Pfand) product.
    depositProductId?: number | null;
    // Borrower — at least one. A company borrower has no column in the person-key
    // table, so it is sent as `person` only when it is a real person; otherwise
    // the row relies on the boat link (see mapKeys).
    person?: { id: number };
    boat?: { id: number };
    keyNumber?: string | null;
    // tinyint(1) flags server-side (paid / refunded). The amount is derived from
    // the key's deposit product, not stored on the assignment row.
    keyFeePayed: boolean;
    keyFeePayedBack: boolean;
    keyFeePayedOn: Date | null;
    keyFeePayedBackOn: Date | null;
    // NOT NULL server-side — falls back to the booking start date.
    fromDate: Date;
    toDate: Date | null;
    note?: string | null;
}

export interface BookingV2ElectricMeterAssignmentDto {
    uuid: string;
    // Edit mode: the existing assignment + its reading id. Sent with `deleted`
    // to remove it, or with `to`/`meterReadingEnd` to disconnect it (kept,
    // untouched existing meters are not re-sent). Newly added meters carry the
    // create fields below instead.
    id?: number;
    reading?: { id: number };
    deleted?: boolean;
    // Disconnect (Trennen): close-out date + end reading. The backend routes an
    // assignment with `id` + `reading.id` through its disconnect path.
    to?: Date | null;
    meterReadingEnd?: number;
    // Links to a created berth assignment by its uuid so the backend can attach
    // the meter to that berth (resolved via the berthAssignment uuid→id map).
    // Absent on delete/disconnect DTOs.
    berthAssignment?: { uuid: string };
    electricMeter?: { id: number };
    from?: Date | null;
    meterReadingStart?: number;
    productType?: { id: number };
    // Billing product (Stromgebühr) + quantity; absent for NO_CHARGE / by-usage.
    product?: { id: number };
    quantity?: number;
}

export interface BookingV2PositionDto {
    // Edit mode: the existing position id → patch it; with `deleted` → soft-delete it.
    id?: number;
    deleted?: boolean;
    // `{ id: null }` for an ad-hoc line — the column is nullable, and a wrapper
    // object (vs. undefined) keeps the backend from dereferencing `.id` on
    // undefined. Same for unit/taxRate.
    product: { id: number | null };
    unit: { id: number | null };
    taxRate: { id: number | null };
    quantity: number;
    price: number;
    title: string; // NOT NULL server-side — always the line's name.
    discountPercentage: number | null;
    vestingPeriodFrom: Date | null;
    vestingPeriodUntil: Date | null;
    // Optional cost center (Kostenstelle); only when the tenant uses them.
    costCenter?: { id: number };
    // Links an electric-meter base-fee line to its meter assignment (by the
    // assignment's uuid), so the backend ties the position to the connection.
    electricMeterAssignmentUuid?: string;
    // Marks a Beziehung one-off line so the backend stamps
    // bcm_person_has_tenant_relations_id (links it to the relation row created in
    // the same transaction). See mapTenantRelationPositions.
    fromTenantRelation?: boolean;
    // Hand-edited price/title of a Beziehung line (#7044); only sent for those lines.
    priceLock?: boolean;
    titleLock?: boolean;
    // Marks a line seeded from a berth's Produkte (#6734) and links it to that
    // berth's assignment by uuid, so the backend stamps
    // bcm_berth_has_assignments_id (Bierdeckel/Export + Leistungszeitraum fallback).
    fromBerth?: boolean;
    berthAssignmentUuid?: string;
    // The backend-evaluated dynamic price for a rule-based line — persisted by the
    // backend as the rule provenance (bcm_dynamic_prices); does NOT change `price`.
    dynamicPrice?: Partial<BcmDynamicPriceDto>;
}

export interface BookingV2SubscriptionDto {
    // Edit mode: the existing subscription id → patch it; with `deleted` → remove it.
    id?: number;
    deleted?: boolean;
    // 'person' | 'company' — only used server-side to skip TenantRelation rows;
    // routing to person/company subscriptions is driven by the booking owner.
    type: 'person' | 'company';
    product: { id: number };
    payableOption: { id: number };
    quantity: number;
    customTitle: string | null;
    customPrice: number | null;
    fromDate: Date | null;
    toDate: Date | null;
    vestingPeriodFrom: Date | null;
    vestingPeriodUntil: Date | null;
    lastDayOfMonth: boolean;
    // Optional cost center (Kostenstelle); only when the tenant uses them.
    costCenter?: { id: number };
    // The backend-evaluated dynamic price for a rule-based subscription — persisted
    // by startPersonSubscription/processDynamicPrice as the rule provenance; does NOT
    // change `customPrice`.
    dynamicPrice?: Partial<BcmDynamicPriceDto>;
}

// `id` present → reference existing; absent → inline create. `street` carries
// the wizard's single free-text address; `newPhone`/`newMail` become rows in
// the related phone/mail tables server-side.
export interface BookingV2CommitPersonDto {
    id?: number;
    firstName?: string | null;
    lastName?: string | null;
    // Salutation enum value (NOT NULL on bcm_persons); defaults to 3 (None).
    salutation?: number | null;
    street?: string | null;
    newPhone?: string;
    newMail?: string;
}

export interface BookingV2CommitCompanyDto {
    id?: number;
    name?: string | null;
    street?: string | null;
    newPhone?: string;
    newMail?: string;
    newContactPerson?: string;
}

export interface BookingV2CommitBoatDto {
    id?: number;
    name?: string | null;
    licensePlate?: string | null;
    // The wizard captures a single length and treats it as LüA throughout
    // (display, fit filter, dynamic price), so it is persisted as such.
    lengthOverall?: number;
    width?: number;
    depth?: number;
    // Existing boat: owner refs let the backend skip its "already the owner?"
    // check (no duplicate BoatOwner row).
    owner?: { id: number };
    ownerCompany?: { id: number };
    // New boat: link to an existing owner picked in the berth step.
    ownerId?: number | null;
    ownerKind?: 'person' | 'company' | null;
}

export interface BookingV2BerthAssignmentDto {
    // Client-generated id linking positions/meters back to this assignment.
    uuid: string;
    // Edit mode: the existing assignment id → patch it; with `deleted` → remove it.
    id?: number;
    deleted?: boolean;
    // `null` → a berth-less assignment (boat held with no berth).
    berth: { id: number } | null;
    from: Date | null;
    to: Date | null;
}

export interface BookingV2TenantRelationDto {
    // `true` → create/update the assignment; `false` (edit only) → remove the
    // existing one identified by `id`.
    captureTenantRelation: boolean;
    // Edit mode: the existing assignment id (patch when capturing, delete when not).
    id?: number;
    tenantRelation?: { id: number };
    fromDate?: Date | null;
    toDate?: Date | null;
    // Billing period — the backend derives `hasSubscription = !toDate` and the
    // scheduler uses `periodFromDate` as the bill-trigger date, so without these
    // an open-ended relation is never billed (NULL trigger). Mirrors the legacy
    // dialog, which fills them from the booking dates.
    periodFromDate?: Date | null;
    periodToDate?: Date | null;
    vestingPeriodFrom?: Date | null;
    vestingPeriodUntil?: Date | null;
    lastDayOfMonth?: boolean;
    // Lagerplatz chosen as a dynamic-price input for the relation's Leistungen.
    // Persisted on the assignment row (mirrors the standalone membership route) so
    // it round-trips on edit — an open-ended relation has no one-off position to
    // carry the provenance. Omitted when no rule branches on a Lagerplatz.
    winterStorage?: { id: number };
}

// Plain snapshot of the facade state the mapper needs — no signals, so the
// mapper stays pure and testable.
// Down-payment input from the reservation step. Mapped to one ad-hoc position.
export interface BookingV2DepositInput {
    amount: number;
    taxRateId: number;
    // Position title (also the Beleg-row label — must match for selectBelegPositions).
    title: string;
    // The reservation period drives the position's vesting (Leistungszeitraum).
    vestingFrom: Date | null;
    vestingUntil: Date | null;
}

export interface BookingV2CommitSnapshot {
    // null = create; a number = editing that booking (drives the id/deleted diff).
    bookingId: number | null;
    // The booking status to persist (preserved on edit; 1 = "open" on create).
    statusId: number;
    // The loaded booking's child ids, for the edit removal diff (empty on create).
    baseline: BookingV2EditBaseline;
    bookingType: BookingV2Type;
    dateRange: BookingV2DateRange;
    // The tenant's day/overnight setting — decides whether a relation product billed
    // per Tag/Übernachtung takes the stay duration as its quantity. (#7017)
    defaultTimeUnit: DefaultBerthReservationTimeUnit | null;
    // Berth occupancies, each with its own window (6609). Falls back to the global
    // date range per field when a window is left blank.
    occupancies: readonly BookingV2BerthOccupancy[];
    boat: BookingV2BoatOption | null;
    person: BookingV2PersonOption | null;
    relation: BookingV2RelationOption | null;
    // Hand-edited Bezeichnung / Einzelpreis of the relation Leistungen (#7044).
    relationLineOverrides: BookingV2RelationLineOverrides;
    // Lagerplatz pricing input (not a booking field) → persisted on the relation
    // assignment so it round-trips on edit. `null` when none chosen.
    winterStorageId: number | null;
    notes: string;
    // ── Reservation mode ──
    reservationValidUntil: Date | null;
    reservationText: string;
    deposit: BookingV2DepositInput | null;
    travelers: readonly BookingV2Traveler[];
    services: readonly BookingV2Service[];
    electricMeters: readonly BookingV2ElectricMeterAssignment[];
    keys: readonly BookingV2KeyAssignment[];
    documents: readonly BookingV2BookingDocument[];
    selectedOrderId: number | null;
}

export function buildBookingV2CommitDto(s: BookingV2CommitSnapshot): BookingV2CommitDto {

    const person = s.person;
    if (!person) {
        throw new Error('booking-v2 commit: no person/organization selected');
    }
    const boat = s.boat;
    if (!boat) {
        throw new Error('booking-v2 commit: no boat selected');
    }

    const isEdit = s.bookingId != null;
    const isPerson = person.kind === 'person';
    const personIsNew = person.origin === 'new';
    const boatIsNew = boat.origin === 'new';

    const from = s.dateRange.from;
    const to = s.dateRange.openEnd ? null : s.dateRange.to;
    const bookingRange: BookingV2DateRange = {
        from,
        to: s.dateRange.openEnd ? null : s.dateRange.to,
        openEnd: s.dateRange.openEnd,
    };

    // "Auto when they own it": flag the booking person as boat owner exactly
    // when the selected person/company already owns the EXISTING boat. The
    // owner refs sent below make the backend treat this as a no-op (no
    // duplicate ownership). For a new boat the owner link is created during the
    // boat insert, so this stays false to avoid a double link.
    const isBoatOwner = !boatIsNew
        && boat.ownerId != null
        && boat.ownerId === person.id
        && boat.ownerKind === person.kind;

    const draft = person.draft;
    const participant: BookingV2CommitPersonDto & BookingV2CommitCompanyDto = personIsNew
        ? isPerson
            ? {
                firstName: draft?.firstName ?? null,
                lastName: draft?.lastName ?? null,
                salutation: draft?.salutation ?? 3, // 3 = Salutation.None
                street: draft?.address ?? null,
                newPhone: draft?.phone ?? undefined,
                newMail: draft?.mail ?? undefined,
            }
            : {
                name: draft?.companyName ?? person.displayName ?? null,
                street: draft?.address ?? null,
                newPhone: draft?.phone ?? undefined,
                newMail: draft?.mail ?? undefined,
                newContactPerson: draft?.contactPerson ?? undefined,
            }
        : { id: person.id };

    const boatDto: BookingV2CommitBoatDto = boatIsNew
        ? {
            name: boat.name,
            licensePlate: boat.licensePlate,
            lengthOverall: boat.length,
            width: boat.width,
            depth: boat.depth,
            ownerId: boat.ownerId ?? undefined,
            ownerKind: boat.ownerKind ?? undefined,
        }
        : {
            id: boat.id,
            owner: boat.ownerId != null && boat.ownerKind === 'person' ? { id: boat.ownerId } : undefined,
            ownerCompany: boat.ownerId != null && boat.ownerKind === 'company' ? { id: boat.ownerId } : undefined,
        };

    // Built up front so meters can reference the primary (first) berth by uuid.
    // On edit, an existing occupancy keeps its assignment id (→ patch, not
    // recreate). Each occupancy carries its OWN window (6609); a blank field falls
    // back to the global booking range so an unfilled card still saves sensibly.
    const berthUuidByBerthId = new Map<number, string>();
    const berthAssignments: BookingV2BerthAssignmentDto[] = s.occupancies
        .filter((o): o is BookingV2BerthOccupancy & { berth: NonNullable<BookingV2BerthOccupancy['berth']> } => o.berth != null)
        .map(occ => {
            const uuid = uuidv4();
            berthUuidByBerthId.set(occ.berth.id, uuid);
            const window = resolveOccupancyCommitWindow(occ, bookingRange);
            return {
                uuid,
                ...(isEdit && occ.assignmentId != null ? { id: occ.assignmentId } : {}),
                berth: { id: occ.berth.id },
                from: window.from,
                to: window.to,
            };
        });

    // Boat is always present; if no berth was selected, still emit one berth-less
    // assignment so the boat links to the booking and meters/keys (which attach to
    // a berth assignment by uuid) have a host. On edit, reuse the existing
    // berth-less assignment id so it is patched, not duplicated.
    if (berthAssignments.length === 0) {
        berthAssignments.push({
            uuid: uuidv4(),
            ...(isEdit && s.baseline.berthlessAssignmentId != null ? { id: s.baseline.berthlessAssignmentId } : {}),
            berth: null,
            from,
            to,
        });
    }
    const primaryBerthUuid = berthAssignments[0]?.uuid ?? '';

    // Edit: any baseline assignment (berthed or berth-less) no longer kept is
    // flagged for deletion.
    if (isEdit) {
        const keptIds = new Set(
            berthAssignments.map(a => a.id).filter((id): id is number => id != null),
        );
        const baselineIds = [...s.baseline.berthAssignmentIds];
        if (s.baseline.berthlessAssignmentId != null) {
            baselineIds.push(s.baseline.berthlessAssignmentId);
        }
        for (const removedId of baselineIds) {
            if (!keptIds.has(removedId)) {
                berthAssignments.push({ uuid: uuidv4(), id: removedId, deleted: true } as unknown as BookingV2BerthAssignmentDto);
            }
        }
    }

    return {
        general: {
            bookingType: s.bookingType,
            note: s.notes.trim() || undefined,
            // Preserved on edit; defaults to 1 ("open") on create.
            status: { id: s.statusId },
            personOrCompany: isPerson ? 'person' : 'company',
            isBoatOwner,
            reservedUntil: s.bookingType === 'reservation' ? s.reservationValidUntil : undefined,
            reservationText: s.bookingType === 'reservation'
                ? (s.reservationText.trim() || undefined)
                : undefined,
            bcm_orders_id: s.selectedOrderId,
        },
        person: isPerson ? participant : undefined,
        company: isPerson ? undefined : participant,
        boat: boatDto,
        berthAssignments,
        travelers: s.travelers.map(t => ({
            firstName: t.firstName,
            lastName: t.lastName,
        })),
        // Primary relation only (additional relations dropped per decision).
        tenantRelationAssignment: mapTenantRelation(s.relation, from, to, s.winterStorageId, isEdit, s.baseline.relationAssignmentId),
        positions: [
            ...mapPositions(s.services, isEdit, s.baseline.positionIds, berthUuidByBerthId),
            ...mapElectricMeterFeePositions(s.electricMeters, isEdit),
            ...mapTenantRelationPositions(
                s.relation, from, to, isEdit, s.baseline.relationPositionIds, s.defaultTimeUnit, s.relationLineOverrides,
            ),
            ...mapDepositPosition(s.bookingType === 'reservation' ? s.deposit : null),
            ...mapKeyDepositPositions(s.keys, isEdit, s.baseline.depositPositionIds),
            ...mapRemovedSubscriptionPositions(s.services, isEdit, s.baseline.subscriptionPositionIds),
        ],
        subscriptions: mapSubscriptions(s.services, isPerson, from, isEdit, s.baseline.subscriptionIds),
        electricMeterAssignments: mapElectricMeters(s.electricMeters, berthUuidByBerthId, primaryBerthUuid, isEdit, s.baseline.meters),
        keyAssignments: mapKeys(s.keys, from, isEdit, s.baseline.keyAssignmentIds),
        documentAssignments: mapDocuments(s.documents, s.baseline.documentIds),
    };
}

// Document attachments → bcm_documents rows. New ones carry the uploaded file
// (bcm_files), the user's title and the chosen category; removed loaded rows go
// out as `{ id, deleted }`. Deleting by absence wiped documents the wizard never
// showed (contract generator output, rows without a file).
function mapDocuments(
    documents: readonly BookingV2BookingDocument[],
    baselineDocumentIds: readonly number[],
): BookingV2DocumentAssignmentDto[] {
    const result: BookingV2DocumentAssignmentDto[] = documents.map(d => d.documentId == null
        ? { bcmFilesId: d.bcmFilesId, title: d.name, documentCategoryId: d.categoryId ?? null }
        : { id: d.documentId, bcmFilesId: d.bcmFilesId, title: d.name });
    appendRemovals(result, baselineDocumentIds, documents.map(d => d.documentId),
        id => ({ id, deleted: true as const }));
    return result;
}

// Key lendings → person/boat key assignments. A "new" key carries its create
// fields (no id) so the backend inserts the catalog key first. A company
// borrower can't be stored — the person-key table only links a person or a
// boat — so a row only persists with a real-person borrower and/or a boat;
// others are dropped. The lending start is NOT NULL server-side, so it falls
// back to the booking start date when the user cleared it.
function mapKeys(
    keys: readonly BookingV2KeyAssignment[],
    bookingFrom: Date | null,
    isEdit: boolean,
    baselineKeyIds: readonly number[],
): BookingV2KeyAssignmentDto[] {
    const result: BookingV2KeyAssignmentDto[] = keys
        .filter(keyHasPersistedBorrower)
        .map(k => {
            const isNew = k.origin === 'new';
            return {
                uuid: k.uuid,
                // Edit: an existing lending keeps its id so the backend patches it.
                ...(isEdit && k.assignmentId != null ? { id: k.assignmentId } : {}),
                key: { id: isNew ? null : k.catalogKeyId },
                keyName: isNew ? k.keyName : undefined,
                keyType: isNew ? k.keyType : undefined,
                keyIdentifier: isNew ? k.keyIdentifier : undefined,
                isUnique: isNew ? k.uniqueness === 'unique' : undefined,
                depositProductId: isNew ? (k.depositProductId ?? null) : undefined,
                person: k.personId != null && k.personKind === 'person' ? { id: k.personId } : undefined,
                boat: k.boatId != null ? { id: k.boatId } : undefined,
                keyNumber: k.keyNumber,
                keyFeePayed: k.keyFeePayedOn != null,
                keyFeePayedBack: k.keyFeePayedBackOn != null,
                keyFeePayedOn: k.keyFeePayedOn,
                keyFeePayedBackOn: k.keyFeePayedBackOn,
                fromDate: k.from ?? bookingFrom ?? new Date(),
                toDate: k.to,
                note: k.note,
            };
        });

    if (isEdit) {
        appendRemovals(result, baselineKeyIds, keys.map(k => k.assignmentId),
            id => ({ uuid: uuidv4(), id, deleted: true } as unknown as BookingV2KeyAssignmentDto));
    }

    return result;
}

// Electric-meter assignments → meter↔boat connections. Each reuses its own
// facade uuid and attaches to the primary berth assignment. The backend
// (ElectricMeterHasBoatsService.create) creates the billing invoice position
// from productType + product + quantity, so no separate position is sent here.
function mapElectricMeters(
    meters: readonly BookingV2ElectricMeterAssignment[],
    berthUuidByBerthId: Map<number, string>,
    primaryBerthUuid: string,
    isEdit: boolean,
    baselineMeters: readonly { assignmentId: number; readingId?: number }[],
): BookingV2ElectricMeterAssignmentDto[] {
    // Edit: only newly added meters are (re)created; kept existing meters are left
    // untouched (re-sending one would close it out via the disconnect path).
    // Proposed (reparent) meters are never created — handled separately below.
    const toCreate = (isEdit ? meters.filter(m => m.assignmentId == null) : meters)
        .filter(m => !m.reparent);

    const result: BookingV2ElectricMeterAssignmentDto[] = toCreate.map(m => ({
        uuid: m.uuid,
        // Attach to the chosen berth occupancy, falling back to the primary one.
        berthAssignment: { uuid: (m.berthId != null ? berthUuidByBerthId.get(m.berthId) : undefined) ?? primaryBerthUuid },
        electricMeter: { id: m.meterId },
        from: m.from,
        meterReadingStart: m.meterReadingStart,
        productType: { id: m.productTypeId },
        product: m.productId != null ? { id: m.productId } : undefined,
        quantity: m.quantity ?? undefined,
    }));

    if (isEdit) {
        // Existing meters staged for disconnect (Trennen): sent with id + reading
        // so the backend closes them out (and powers off web-switchable meters)
        // instead of hard-deleting them.
        for (const m of meters.filter(m => m.assignmentId != null && m.disconnected && m.readingId != null)) {
            result.push({
                uuid: m.uuid,
                id: m.assignmentId,
                electricMeter: { id: m.meterId },
                reading: { id: m.readingId as number },
                to: m.to,
                meterReadingEnd: m.meterReadingEnd ?? undefined,
                product: m.productId != null ? { id: m.productId } : undefined,
            });
        }

        const keptIds = new Set(
            meters.map(m => m.assignmentId).filter((id): id is number => id != null),
        );
        for (const m of baselineMeters) {
            if (!keptIds.has(m.assignmentId)) {
                result.push({
                    uuid: uuidv4(),
                    id: m.assignmentId,
                    reading: m.readingId != null ? { id: m.readingId } : undefined,
                    deleted: true,
                });
            }
        }
    }

    // Take-over ("übernehmen"): an existing OPEN standalone assignment is
    // re-parented onto this booking. Send ONLY id + the target berth uuid — no
    // electricMeter (would route to create) and no reading (would route to
    // disconnect). Runs in both modes; proposals never appear in baselineMeters.
    for (const m of meters.filter(m => m.reparent && m.assignmentId != null)) {
        result.push({
            uuid: m.uuid,
            id: m.assignmentId,
            berthAssignment: {
                uuid: (m.berthId != null ? berthUuidByBerthId.get(m.berthId) : undefined) ?? primaryBerthUuid,
            },
        });
    }

    return result;
}

// By-usage base fees (Grundgebühr) → standalone invoice positions, each linked
// to its meter assignment by uuid. Created only for meters being (re)created —
// kept existing meters keep the fee position they were saved with.
function mapElectricMeterFeePositions(
    meters: readonly BookingV2ElectricMeterAssignment[],
    isEdit: boolean,
): BookingV2PositionDto[] {
    // Proposed (reparent) meters carry no additionalFee and keep their existing
    // billing — never spawn a fee position for them.
    const toCreate = (isEdit ? meters.filter(m => m.assignmentId == null) : meters)
        .filter(m => !m.reparent);
    return toCreate
        .filter(m => m.additionalFee != null)
        .map(m => {
            const fee = m.additionalFee as NonNullable<BookingV2ElectricMeterAssignment['additionalFee']>;
            return {
                product: { id: fee.productId },
                unit: { id: fee.unitId },
                taxRate: { id: fee.taxRateId },
                quantity: fee.quantity,
                price: fee.price,
                title: fee.title,
                discountPercentage: null,
                vestingPeriodFrom: null,
                vestingPeriodUntil: null,
                electricMeterAssignmentUuid: m.uuid,
            };
        });
}

// A seeded berth-product line that must not reach the Beleg: quantity 0 (the
// wizard lists it so the user can fill it in — an untouched 0 is simply not
// billed), or the legacy zero-total rule (`ignoreFreeOfChargePosition` on the berth
// product). Only reconcile-managed lines are subject to this: a line recovered on
// edit carries a berthOrigin purely for its berth link and stays an ordinary service.
export function isDroppedBerthLine(service: BookingV2Service): boolean {
    if (!service.berthOrigin?.managed) {
        return false;
    }
    return !(service.quantity > 0)
        || shouldOmitZeroPriceBerthPosition(
            true,
            service.berthOrigin.ignoreFreeOfChargePosition,
            service.price,
            service.quantity,
        );
}

// One-off service lines → invoice positions. Catalog products carry a real id;
// ad-hoc lines send `{ id: null }` (the column is nullable). `price` is sent
// as-is; a rule-based line also carries its evaluated `dynamicPrice` so the
// backend persists the rule provenance (it does not change `price`).
//
// A berth-product line (#6734) additionally carries `fromBerth` + the uuid of its
// berth assignment, and is skipped when it must not be billed at all — quantity 0
// (the ticket's "nur Menge > 0 übernehmen") or the legacy zero-total rule. An
// already saved such line is soft-deleted instead of silently kept.
function mapPositions(
    services: readonly BookingV2Service[],
    isEdit: boolean,
    baselinePositionIds: readonly number[],
    berthUuidByBerthId: Map<number, string>,
): BookingV2PositionDto[] {
    // Settled (protected) positions are never sent — the backend leaves untouched
    // anything absent from the DTO.
    const billable = services.filter(s => !s.isSubscription && !s.isProtected);

    const result: BookingV2PositionDto[] = billable
        .filter(s => !isDroppedBerthLine(s))
        .map(s => ({
            // Edit: an existing position keeps its id so the backend patches it.
            ...(isEdit && s.positionId != null ? { id: s.positionId } : {}),
            product: { id: s.productId },
            unit: { id: s.unitId },
            taxRate: { id: s.taxRateId },
            quantity: s.quantity,
            price: s.price,
            title: s.name,
            discountPercentage: s.discount ?? null,
            vestingPeriodFrom: s.serviceStart,
            vestingPeriodUntil: s.serviceEndOpen ? null : s.serviceEnd,
            costCenter: s.costCenterId != null ? { id: s.costCenterId } : undefined,
            // Seeded berth products are flagged; a line recovered on edit only keeps
            // its berth link so the backend does not re-resolve (and null) the FK.
            ...(s.berthOrigin?.managed ? { fromBerth: true } : {}),
            ...(s.berthOrigin && berthUuidByBerthId.has(s.berthOrigin.berthId)
                ? { berthAssignmentUuid: berthUuidByBerthId.get(s.berthOrigin.berthId) }
                : {}),
            ...(s.dynamicPrice ? { dynamicPrice: s.dynamicPrice } : {}),
        }));

    // A dropped line that is already persisted must be removed, not just omitted.
    for (const s of billable) {
        if (isDroppedBerthLine(s) && s.positionId != null) {
            result.push({ id: s.positionId, deleted: true } as unknown as BookingV2PositionDto);
        }
    }

    if (isEdit) {
        appendRemovals(result, baselinePositionIds,
            services.filter(s => !s.isSubscription).map(s => s.positionId),
            id => ({ id, deleted: true } as unknown as BookingV2PositionDto));
    }

    return result;
}

// Removing an Abo also removes its still-open positions — the FK is set to NULL on delete,
// which would leave them billable on the booking as stray lines. (#7044)
function mapRemovedSubscriptionPositions(
    services: readonly BookingV2Service[],
    isEdit: boolean,
    positionIdsBySubscription: Readonly<Record<number, readonly number[]>>,
): BookingV2PositionDto[] {
    if (!isEdit) {
        return [];
    }
    const kept = new Set(services.filter(s => s.isSubscription && s.subscriptionId != null).map(s => s.subscriptionId));
    return Object.entries(positionIdsBySubscription)
        .filter(([subscriptionId]) => !kept.has(Number(subscriptionId)))
        .flatMap(([, ids]) => ids.map(id => ({ id, deleted: true } as unknown as BookingV2PositionDto)));
}

// Reservation down payment → one ad-hoc invoice position. Always a fresh line
// (create-only — the deposit card is not offered on edit), qty 1, no product.
function mapDepositPosition(deposit: BookingV2DepositInput | null): BookingV2PositionDto[] {
    if (!deposit || !(deposit.amount > 0)) {
        return [];
    }
    return [{
        product: { id: null },
        unit: { id: null },
        taxRate: { id: deposit.taxRateId },
        quantity: 1,
        price: deposit.amount,
        title: deposit.title,
        discountPercentage: null,
        vestingPeriodFrom: deposit.vestingFrom,
        vestingPeriodUntil: deposit.vestingUntil,
    }];
}

// Subscription service lines → product subscriptions. A subscription needs a
// catalog product and a billing interval (payableOption); incomplete rows are
// skipped so the backend never dereferences a missing reference.
function mapSubscriptions(
    services: readonly BookingV2Service[],
    isPerson: boolean,
    bookingFrom: Date | null,
    isEdit: boolean,
    baselineSubscriptionIds: readonly number[],
): BookingV2SubscriptionDto[] {
    const result: BookingV2SubscriptionDto[] = services
        .filter((s): s is BookingV2Service & {
            productId: number;
            subscriptionConfig: BookingV2SubscriptionConfig & { payableOptionId: number };
        } =>
            !!s.isSubscription
            && s.productId != null
            && s.subscriptionConfig?.payableOptionId != null,
        )
        .map(s => {
            const cfg = s.subscriptionConfig;
            return {
                // Edit: an existing subscription keeps its id so the backend patches it.
                ...(isEdit && s.subscriptionId != null ? { id: s.subscriptionId } : {}),
                type: isPerson ? 'person' : 'company',
                product: { id: s.productId },
                payableOption: { id: cfg.payableOptionId },
                quantity: s.quantity,
                // The scheduler titles every later position with customTitle (else the product
                // name), so a rule-priced Abo sends the name it shows, rule name included (#7044).
                customTitle: s.hasDynamicPrice || !serviceFollowsAutoName(s) ? s.name : null,
                customPrice: s.price,
                // fromDate is NOT NULL server-side — fall back to the booking
                // start when the user cleared the subscription start.
                fromDate: cfg.fromDate ?? bookingFrom ?? new Date(),
                toDate: cfg.toDate,
                vestingPeriodFrom: cfg.vestingPeriodFrom,
                vestingPeriodUntil: cfg.vestingPeriodUntil,
                lastDayOfMonth: cfg.lastDayOfMonth,
                costCenter: s.costCenterId != null ? { id: s.costCenterId } : undefined,
                ...(s.dynamicPrice ? { dynamicPrice: s.dynamicPrice } : {}),
            };
        });

    if (isEdit) {
        appendRemovals(result, baselineSubscriptionIds,
            services.filter(s => s.isSubscription).map(s => s.subscriptionId),
            id => ({ id, deleted: true } as unknown as BookingV2SubscriptionDto));
    }

    return result;
}

// For each baseline child id no longer present, push a `{ id, deleted }` stub.
function appendRemovals<T>(
    target: T[],
    baselineIds: readonly number[],
    currentIds: readonly (number | undefined)[],
    makeStub: (id: number) => T,
): void {
    const kept = new Set(currentIds.filter((id): id is number => id != null));
    for (const id of baselineIds) {
        if (!kept.has(id)) {
            target.push(makeStub(id));
        }
    }
}

// Create/update the relation assignment, or (edit, cleared) delete the existing one.
// The period/vesting dates are filled from the booking dates (like the legacy
// dialog): the backend derives `hasSubscription = !toDate`, and the scheduler
// bills an open-ended relation from `periodFromDate` — without it the relation is
// never billed. See mapTenantRelationPositions for the fixed-end one-off lines.
function mapTenantRelation(
    relation: BookingV2RelationOption | null,
    from: Date | null,
    to: Date | null,
    winterStorageId: number | null,
    isEdit: boolean,
    baselineRelationAssignmentId: number | null,
): BookingV2TenantRelationDto | undefined {
    if (relation) {
        const payableOption = relation.payableOption ?? null;
        return {
            captureTenantRelation: true,
            ...(isEdit && relation.assignmentId != null ? { id: relation.assignmentId } : {}),
            tenantRelation: { id: relation.id },
            fromDate: from,
            toDate: to,
            periodFromDate: from,
            periodToDate: to,
            vestingPeriodFrom: from,
            // Fixed end → the end date; open end → the first period's end.
            vestingPeriodUntil: to ?? (from && payableOption ? getVestingPeriodUntil(from, payableOption) : null),
            lastDayOfMonth: false,
            ...winterStorageAssignmentPatch(winterStorageId),
        };
    }

    if (isEdit && baselineRelationAssignmentId != null) {
        return { captureTenantRelation: false, id: baselineRelationAssignmentId };
    }

    return undefined;
}
